import { billChangedForPromotions, saleLineKey } from "../promotions/release";
import type { Sale } from "../promotions/types";

/**
 * What changed between two states of the sale, derived ONCE (docs/architecture/SALE_EFFECTS.md §2, §6 E1a;
 * DISCOUNT_V2_PLAN.md §9.10 decisions 60 to 65). v1's effects each diffed the two sales their own way
 * (`hasBillChangedForLoyalty`, `isCartDeletionScenario`, the customer test); here the diff is one pure
 * function and every effect reads its answer.
 *
 * Pure: two sales and the write's intent in, a list of typed changes out. Nothing here touches a store,
 * the network or a partner.
 */

/**
 * What a write says about itself. The v2 write path knows when it places an order and when it throws a
 * cart away, so it says so (decision 61): an emptied cart is NOT a discard unless told, and a write that
 * keeps the promotions says so instead of arming a module flag beside the dispatch (v1's
 * `skipNextBillChangeInvalidation`).
 */
export type SaleWriteIntent = {
  ending?: "placed" | "discarded";
  /** The promotions on this bill stay valid across this write (a reward re-tied to a new item). */
  keepsPromotions?: boolean;
};

/**
 * A write tells its intent on the `saveSaleData` action it dispatches (`ordering/writePath` › `repriceSale`'s
 * `intent`), so whoever hears the save hears the intent with it: v1's listener hands a told write to v2's effects
 * (E3). An action with no intent is v1's own code writing.
 */
export function withToldIntent<Action extends object>(action: Action, intent: SaleWriteIntent): Action & { meta: { saleWriteIntent: SaleWriteIntent } } {
  return { ...action, meta: { saleWriteIntent: intent } };
}

/** The intent a saved sale's action told, or `undefined` when it told none (`withToldIntent`). */
export function toldIntent(action: unknown): SaleWriteIntent | undefined {
  return (action as { meta?: { saleWriteIntent?: SaleWriteIntent } } | null)?.meta?.saleWriteIntent;
}

/** A line as the changes name it: keyed `itemId` + `itemNumber` as `billChangedForPromotions` keys it. */
export type ChangedLine = { key: string; itemId: unknown; itemNumber: unknown; quantity: number | undefined };

export type SaleChange =
  | { kind: "linesAdded"; lines: ChangedLine[] }
  | { kind: "linesRemoved"; lines: ChangedLine[] }
  | { kind: "quantityChanged"; lines: (ChangedLine & { from: number | undefined })[] }
  /** Only when the new sale has a customer and it is not the old one (v1's `customerLoyaltyFetchEffect`). */
  | { kind: "customerChanged"; from: string | undefined; to: string }
  | { kind: "billChangedForPromotions" }
  /** A different sale: the `mid` differs, including the first write of a new sale. */
  | { kind: "saleSwitched"; from: string | undefined; to: string | undefined }
  | { kind: "placed" }
  | { kind: "discarded" };

export type SaleChangeKind = SaleChange["kind"];

type SaleLine = NonNullable<Sale["saleItems"]>[number];


const changedLine = (line: SaleLine): ChangedLine => ({
  key: saleLineKey(line),
  itemId: line.itemId,
  itemNumber: line.itemNumber,
  quantity: line.quantity,
});

/** Lines by key; a key repeated on one sale keeps its last line, as the bill-change rule's quantity map does. */
const byKey = (lines: readonly SaleLine[] | undefined): Map<string, SaleLine> =>
  new Map((lines ?? []).map((line) => [saleLineKey(line), line]));

function lineChanges(previous: Sale | null | undefined, next: Sale | null | undefined): SaleChange[] {
  const before = byKey(previous?.saleItems);
  const after = byKey(next?.saleItems);
  const added = [...after].filter(([key]) => !before.has(key)).map(([, line]) => changedLine(line));
  const removed = [...before].filter(([key]) => !after.has(key)).map(([, line]) => changedLine(line));
  const requantified = [...after]
    .filter(([key, line]) => before.has(key) && before.get(key)?.quantity !== line.quantity)
    .map(([key, line]) => ({ ...changedLine(line), from: before.get(key)?.quantity }));

  const changes: SaleChange[] = [];
  if (added.length > 0) changes.push({ kind: "linesAdded", lines: added });
  if (removed.length > 0) changes.push({ kind: "linesRemoved", lines: removed });
  if (requantified.length > 0) changes.push({ kind: "quantityChanged", lines: requantified });
  return changes;
}

const customerRefIdOf = (sale: Sale | null | undefined): string | undefined => sale?.customerInfo?.customerRefId;

/**
 * Every change between `previous` and `next`, in a fixed order: the sale switched, the lines, the customer,
 * the bill under its promotions, then the told endings. The line changes are the raw difference between the
 * two sales, so on a switch every line of the new sale is "added"; an effect that must not act across sales
 * reads `saleSwitched` too (`planBillChange` does, by `mid`).
 *
 * 🔴 **`discarded` and `placed` come from the intent alone** (decision 61). v1's `isCartDeletionScenario`
 * took any cart that lost its last line for a discard, with no `mid` check, so emptying a cart by hand
 * released and refunded it and the bill-change refund then refunded the same line again.
 */
export function saleChanges(
  previous: Sale | null | undefined,
  next: Sale | null | undefined,
  intent: SaleWriteIntent = {},
): SaleChange[] {
  const changes: SaleChange[] = [];
  if (previous?.mid !== next?.mid) changes.push({ kind: "saleSwitched", from: previous?.mid, to: next?.mid });
  changes.push(...lineChanges(previous, next));

  const customerBefore = customerRefIdOf(previous);
  const customerNow = customerRefIdOf(next);
  if (customerNow && customerNow !== customerBefore) {
    changes.push({ kind: "customerChanged", from: customerBefore, to: customerNow });
  }

  if (billChangedForPromotions(previous, next)) changes.push({ kind: "billChangedForPromotions" });
  if (intent.ending === "placed") changes.push({ kind: "placed" });
  if (intent.ending === "discarded") changes.push({ kind: "discarded" });
  return changes;
}
